/**
 * Service for managing church data through the ConnectPath API.
 * 
 * This service provides real API implementations for church management functionality
 * including church profile management, ministry organization, and member tracking.
 * It communicates with backend endpoints to manage church-related data.
 * 
 * This service provides methods for:
 * - Retrieving and updating church profiles
 * - Managing church locations and service times
 * - Ministry management and organization
 * - Church member listing and management
 * - Church statistics and analytics
 * 
 * @example
 * const churchService = new ApiChurchService(process.env.VITE_API_URL || '/api/v1');
 * const church = await churchService.getChurch('church-1');
 */
import { BaseService } from '../base/BaseService';
import { IChurchService } from '../../interfaces/IChurchService';
import {
  Church,
  ChurchLocation,
  ServiceTime,
  ServiceTimeException,
  ServiceInstance,
  ChurchStatistics,
  LeadershipMember,
  ChurchLeader,
  ChurchLeaderRequest,
  ChurchSettings,
  CreateChurchRequest,
  UpdateChurchRequest,
  Denomination,
  ChurchAIContext,
  ProfileCompletionStatus,
  SuggestedDefaults,
  ChurchSizeCategory,
  PrimaryFocus,
  BiggestChallenge,
  ChurchPriority,
  GrowthGoals,
  ResourceLevel,
  SmartGoal,
  CustomKPI
} from '../../../types/church';
import { Ministry } from '../../../types/ministry';
import { ChurchMember } from '../../../types/member';
import { Event, CreateEventRequest, UpdateEventRequest, EventFilters } from '../../../types/event';
import { api } from '../../api';

export class ApiChurchService extends BaseService implements IChurchService {
  constructor(apiUrl: string) {
    super('ApiChurchService');
    // Note: apiUrl parameter kept for compatibility but not used
    // as we use the centralized api instance
  }

  /**
   * Retrieve church details from the API.
   * 
   * @param {string} id - The church ID
   * @returns {Promise<Church>} Church data
   * @throws {Error} If the API request fails
   */
  async getChurch(id: string): Promise<Church> {
    const response = await api.get(`/churches/${id}/`);
    const church = response.data;
    
    // Transform service_times to serviceTimes with camelCase fields if present
    if (church.service_times) {
      church.serviceTimes = church.service_times.map((st: any) => ({
        id: st.id,
        dayOfWeek: st.day_of_week,
        startTime: st.start_time,
        endTime: st.end_time,
        serviceType: st.service_type,
        location: st.location,
        description: st.description
      }));
    }
    
    // Map primary location to structuredAddress for backward compatibility
    if (church.locations && church.locations.length > 0) {
      const primaryLocation = church.locations.find((loc: any) => loc.is_primary) || church.locations[0];
      if (primaryLocation) {
        church.structuredAddress = {
          street1: primaryLocation.address_line1 || '',
          street2: primaryLocation.address_line2 || '',
          city: primaryLocation.city || '',
          state: primaryLocation.state || '',
          zipCode: primaryLocation.postal_code || '',
          country: primaryLocation.country || 'USA'
        };
      }
    }
    
    return church;
  }

  async updateChurch(id: string | number, data: UpdateChurchRequest): Promise<Church> {
    // Convert structuredAddress to flat address fields for backend
    const requestData = { ...data };
    if (data.structuredAddress) {
      requestData.address = data.structuredAddress.street1;
      requestData.city = data.structuredAddress.city;
      requestData.state = data.structuredAddress.state;
      requestData.zip_code = data.structuredAddress.zipCode;
      delete requestData.structuredAddress;
    }
    
    const response = await api.patch(`/churches/${id}/`, requestData);
    const church = response.data;
    
    // Map primary location to structuredAddress for consistency
    if (church.locations && church.locations.length > 0) {
      const primaryLocation = church.locations.find((loc: any) => loc.is_primary) || church.locations[0];
      if (primaryLocation) {
        church.structuredAddress = {
          street1: primaryLocation.address_line1 || '',
          street2: primaryLocation.address_line2 || '',
          city: primaryLocation.city || '',
          state: primaryLocation.state || '',
          zipCode: primaryLocation.postal_code || '',
          country: primaryLocation.country || 'USA'
        };
      }
    }
    
    return church;
  }

  async getCurrentChurch(): Promise<Church> {
    const response = await api.get('/current/');
    return response.data;
  }

  async getLocations(churchId: string): Promise<ChurchLocation[]> {
    const response = await api.get(`/churches/${churchId}/locations/`);
    const data = response.data;
    return data.results || data || [];
  }

  async createLocation(churchId: string, location: Partial<ChurchLocation>): Promise<ChurchLocation> {
    const response = await api.post(`/churches/${churchId}/locations/`, location);
    return response.data;
  }

  async updateLocation(churchId: string, locationId: string, updates: Partial<ChurchLocation>): Promise<ChurchLocation> {
    const response = await api.patch(`/churches/${churchId}/locations/${locationId}/`, updates);
    return response.data;
  }

  async deleteLocation(churchId: string, locationId: string): Promise<void> {
    await api.delete(`/churches/${churchId}/locations/${locationId}/`);
  }

  async getLocation(churchId: string, locationId: string): Promise<ChurchLocation> {
    const response = await api.get(`/churches/${churchId}/locations/${locationId}/`);
    return response.data;
  }

  async createLocationSpace(churchId: string, locationId: string, space: Partial<ChurchLocation>): Promise<ChurchLocation> {
    const response = await api.post(`/churches/${churchId}/locations/${locationId}/spaces/`, space);
    return response.data;
  }

  async updateLocationSpace(churchId: string, locationId: string, spaceId: string, updates: Partial<ChurchLocation>): Promise<ChurchLocation> {
    const response = await api.patch(`/churches/${churchId}/locations/${locationId}/spaces/${spaceId}/`, updates);
    return response.data;
  }

  async deleteLocationSpace(churchId: string, locationId: string, spaceId: string): Promise<void> {
    await api.delete(`/churches/${churchId}/locations/${locationId}/spaces/${spaceId}/`);
  }

  // Legacy methods for backward compatibility
  async addLocation(churchId: string, location: Omit<ChurchLocation, 'id'>): Promise<ChurchLocation> {
    return this.createLocation(churchId, location);
  }

  async getServiceTimes(churchId: string): Promise<ServiceTime[]> {
    const response = await api.get(`/churches/${churchId}/service-times/`);
    const data = response.data;
    const serviceTimes = data.results || data || [];
    
    // Transform snake_case API response to camelCase for frontend
    return serviceTimes.map((st: any) => ({
      id: st.id,
      dayOfWeek: st.day_of_week,
      startTime: st.start_time,
      endTime: st.end_time,
      serviceType: st.service_type,
      location: st.location,
      description: st.description
    }));
  }

  async addServiceTime(churchId: string, serviceTime: Omit<ServiceTime, 'id'>): Promise<ServiceTime> {
    const response = await api.post(`/churches/${churchId}/service-times/`, serviceTime);
    
    // Transform snake_case API response to camelCase for frontend
    const data = response.data;
    return {
      id: data.id,
      dayOfWeek: data.day_of_week,
      startTime: data.start_time,
      endTime: data.end_time,
      serviceType: data.service_type,
      location: data.location,
      description: data.description
    };
  }

  async updateServiceTime(churchId: string, serviceTimeId: string, data: Partial<ServiceTime>): Promise<ServiceTime> {
    // Transform camelCase to snake_case for API
    const apiData: any = {};
    if (data.dayOfWeek !== undefined) apiData.day_of_week = data.dayOfWeek;
    if (data.startTime !== undefined) apiData.start_time = data.startTime;
    if (data.endTime !== undefined) apiData.end_time = data.endTime;
    if (data.serviceType !== undefined) apiData.service_type = data.serviceType;
    if (data.location !== undefined) apiData.location = data.location;
    if (data.description !== undefined) apiData.description = data.description;
    
    const response = await api.patch(`/churches/${churchId}/service-times/${serviceTimeId}/`, apiData);
    
    // Transform snake_case API response to camelCase for frontend
    const result = response.data;
    return {
      id: result.id,
      dayOfWeek: result.day_of_week,
      startTime: result.start_time,
      endTime: result.end_time,
      serviceType: result.service_type,
      location: result.location,
      description: result.description
    };
  }

  async deleteServiceTime(churchId: string, serviceTimeId: string): Promise<void> {
    await api.delete(`/churches/${churchId}/service-times/${serviceTimeId}/`);
  }

  // ===== Service Time Instance Methods =====
  // These methods handle cancellation, deletion, and modification of specific service occurrences

  /**
   * Get service instances for a specific service time within a date range.
   * Returns generated instances with exception handling (cancelled, modified, deleted).
   */
  async getServiceInstances(
    churchId: string,
    serviceTimeId: string,
    startDate: string,
    endDate: string
  ): Promise<ServiceInstance[]> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate
    });

    const response = await api.get(
      `/churches/${churchId}/service-times/${serviceTimeId}/instances/?${params}`
    );
    return response.data.instances || [];
  }

  /**
   * Get all service instances for a church within a date range.
   * Returns all generated instances across all service times with exception handling.
   */
  async getAllServiceInstances(
    churchId: string,
    startDate: string,
    endDate: string
  ): Promise<ServiceInstance[]> {
    const params = new URLSearchParams({
      start_date: startDate,
      end_date: endDate
    });

    const response = await api.get(
      `/churches/${churchId}/service-times/all-instances/?${params}`
    );
    return response.data.instances || [];
  }

  /**
   * Cancel a specific service instance occurrence.
   * The instance will still appear on the calendar but greyed out with the cancellation reason.
   */
  async cancelServiceInstance(
    churchId: string,
    serviceTimeId: string,
    date: string,
    reason?: string
  ): Promise<ServiceTimeException> {
    const response = await api.post(
      `/churches/${churchId}/service-times/${serviceTimeId}/cancel-instance/`,
      { date, reason: reason || '' }
    );
    return response.data;
  }

  /**
   * Delete a specific service instance occurrence.
   * The instance will be completely removed from the calendar.
   */
  async deleteServiceInstance(
    churchId: string,
    serviceTimeId: string,
    date: string
  ): Promise<void> {
    await api.post(
      `/churches/${churchId}/service-times/${serviceTimeId}/delete-instance/`,
      { date }
    );
  }

  /**
   * Edit a specific service instance occurrence.
   * Creates a modified exception with override values for this instance only.
   */
  async editServiceInstance(
    churchId: string,
    serviceTimeId: string,
    date: string,
    updates: {
      start_time?: string;
      end_time?: string;
      location?: string;
      description?: string;
    }
  ): Promise<ServiceTimeException> {
    const response = await api.post(
      `/churches/${churchId}/service-times/${serviceTimeId}/edit-instance/`,
      { date, ...updates }
    );
    return response.data;
  }

  /**
   * Restore a cancelled or deleted service instance.
   * Removes the exception, restoring the instance to its original state.
   */
  async restoreServiceInstance(
    churchId: string,
    serviceTimeId: string,
    date: string
  ): Promise<void> {
    await api.post(
      `/churches/${churchId}/service-times/${serviceTimeId}/restore-instance/`,
      { date }
    );
  }

  async getMinistries(churchId: string): Promise<Ministry[]> {
    const response = await api.get(`/churches/${churchId}/ministries/`);
    const data = response.data;
    return data.results || data || [];
  }

  async getMinistry(ministryId: string): Promise<Ministry> {
    const response = await api.get(`/ministries/${ministryId}/`);
    return response.data;
  }

  async createMinistry(churchId: string, ministry: Omit<Ministry, 'id' | 'createdAt' | 'updatedAt'>): Promise<Ministry> {
    // Ministry type uses snake_case: leader, is_active
    const payload = {
      name: ministry.name,
      description: ministry.description,
      leader: ministry.leader || null,
      is_active: ministry.is_active ?? true,
      ministry_type: (ministry as any).ministry_type || 'other',
    };
    // Use add_ministry action endpoint (ministries/ only allows GET due to ChurchViewSet action)
    const response = await api.post(`/churches/${churchId}/add_ministry/`, payload);
    return response.data;
  }

  async updateMinistry(churchId: string, ministryId: string, data: Partial<Ministry>): Promise<Ministry> {
    // Ministry type uses snake_case: leader, is_active
    const payload: Record<string, any> = {};
    if (data.name !== undefined) payload.name = data.name;
    if (data.description !== undefined) payload.description = data.description;
    if (data.leader !== undefined) payload.leader = data.leader || null;
    if (data.is_active !== undefined) payload.is_active = data.is_active;
    if ((data as any).ministry_type !== undefined) payload.ministry_type = (data as any).ministry_type;

    const response = await api.patch(`/churches/${churchId}/ministries/${ministryId}/`, payload);
    return response.data;
  }

  async deleteMinistry(churchId: string, ministryId: string): Promise<void> {
    await api.delete(`/churches/${churchId}/ministries/${ministryId}/`);
  }

  async getMinistryMembers(ministryId: string): Promise<ChurchMember[]> {
    const response = await api.get(`/ministries/${ministryId}/members/`);
    const data = response.data;
    return data.results || data || [];
  }

  async addMinistryMember(ministryId: string, memberId: string): Promise<void> {
    await api.post(`/ministries/${ministryId}/members/`, { member_id: memberId });
  }

  async removeMinistryMember(ministryId: string, memberId: string): Promise<void> {
    await api.delete(`/ministries/${ministryId}/members/${memberId}/`);
  }

  async getMembers(churchId: string): Promise<ChurchMember[]> {
    // Use the new /members/ endpoint which includes profile_picture_url
    // The old /churches/{id}/members/ endpoint uses the deprecated ChurchMember model
    // Pass churchId as query param for explicit filtering (endpoint also uses X-Selected-Church-ID header)
    const response = await api.get(`/members/`, { params: { church: churchId } });
    const data = response.data;
    const members = data.results || data || [];
    console.log(`[ApiChurchService] getMembers for church ${churchId}:`, members.length, 'members');

    // Transform the API response to match the expected ChurchMember format
    // The new /members/ API returns snake_case fields
    return members.map((member: any) => ({
      ...member,
      // Map snake_case to camelCase for frontend
      firstName: member.first_name || '',
      lastName: member.last_name || '',
      email: member.email || '',
      phone: member.phone || '',
      membershipStatus: member.membership_status || 'active',
      // Profile picture URL - map snake_case to camelCase for frontend
      profilePictureUrl: member.profile_picture_url || undefined,
      // Gender for avatar display
      gender: member.gender || undefined,
      // Keep display_name for compatibility
      name: member.display_name || `${member.first_name || ''} ${member.last_name || ''}`.trim()
    }));
  }

  async addMember(churchId: string, member: Partial<ChurchMember>): Promise<ChurchMember> {
    const response = await api.post(`/churches/${churchId}/members/`, member);
    return response.data;
  }

  async updateMember(memberId: string, data: Partial<ChurchMember>): Promise<ChurchMember> {
    const response = await api.patch(`/members/${memberId}/`, data);
    return response.data;
  }



  async removeMember(churchId: string, memberId: string): Promise<void> {
    await api.delete(`/churches/${churchId}/members/${memberId}/`);
  }

  async getResignedMembers(churchId: string): Promise<any[]> {
    const response = await api.get(`/churches/${churchId}/resigned_members/`);
    const data = response.data;
    const members = data.results || data || [];
    
    // Transform to match frontend expectations
    return members.map((member: any) => ({
      ...member,
      firstName: member.user_info?.name?.split(' ')[0] || '',
      lastName: member.user_info?.name?.split(' ').slice(1).join(' ') || '',
      email: member.user_info?.email || '',
      phone: member.user_info?.phone || member.phone || '',
      user_info: member.user_info,
      name: member.user_info?.name || '',
      resignationDate: member.resignation_date,
      resignationReason: member.resignation_reason
    }));
  }

  async promoteMemberToUser(memberId: string, promotionData: {
    password: string;
    role: 'admin' | 'staff' | 'volunteer' | 'member';
    church_staff_role?: 'admin' | 'pastor' | 'staff' | 'volunteer';
    send_welcome_email?: boolean;
  }): Promise<{ user: any; member: any }> {
    const response = await api.post(`/members/${memberId}/promote/`, promotionData);
    return response.data;
  }

  async getPromotableMembers(churchId?: string): Promise<ChurchMember[]> {
    const params = churchId ? `?church_id=${churchId}` : '';
    const response = await api.get(`/members/promotable/${params}`);
    const members = response.data;
    
    // Transform to match frontend expectations
    return members.map((member: any) => ({
      ...member,
      firstName: member.first_name || '',
      lastName: member.last_name || '',
      email: member.email || '',
      phone: member.phone || '',
      canBePromoted: !member.user && member.email
    }));
  }

  async getChurchStats(churchId: string): Promise<{
    totalMembers: number;
    activeMembers: number;
    ministries: number;
    serviceTimes: number;
    locations: number;
  }> {
    const response = await api.get(`/churches/${churchId}/stats/`);
    return response.data;
  }

  async uploadLogo(churchId: string | number, file: File): Promise<Church> {
    const formData = new FormData();
    formData.append('logo', file);
    
    const response = await api.post(`/churches/${churchId}/upload_logo/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    
    return response.data;
  }

  async updateSettings(churchId: string | number, settings: ChurchSettings): Promise<Church> {
    const response = await api.patch(`/churches/${churchId}/settings/`, settings);
    return response.data;
  }

  /**
   * Get church statistics from the API.
   * 
   * @param {string | number} id - The church ID
   * @returns {Promise<ChurchStatistics>} Church statistics data
   * @throws {Error} If the API request fails
   */
  async getChurchStatistics(id: string | number): Promise<ChurchStatistics> {
    try {
      const response = await api.get(`/churches/${id}/statistics/`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('404: Statistics endpoint not found');
      }
      throw error;
    }
  }

  // Implement remaining IChurchService methods
  
  async getChurches(filters?: {
    isActive?: boolean;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ churches: Church[]; total: number }> {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.isActive !== undefined) params.append('is_active', String(filters.isActive));
      if (filters.search) params.append('search', filters.search);
      if (filters.limit) params.append('limit', String(filters.limit));
      if (filters.offset) params.append('offset', String(filters.offset));
    }
    
    const response = await api.get(`/churches/?${params}`);
    const data = response.data;
    return {
      churches: data.results || [],
      total: data.count || 0
    };
  }

  async createChurch(church: CreateChurchRequest): Promise<Church> {
    const response = await api.post('/churches/', church);
    return response.data;
  }

  async deleteChurch(id: string | number): Promise<void> {
    await api.delete(`/churches/${id}/`);
  }

  async updateServiceTimes(id: string | number, serviceTimes: ServiceTime[]): Promise<Church> {
    const response = await api.put(`/churches/${id}/service_times/`, { service_times: serviceTimes });
    return response.data;
  }


  async updateLeadership(id: string | number, leadership: LeadershipMember[]): Promise<Church> {
    const response = await api.put(`/churches/${id}/leadership/`, { leadership: leadership });
    return response.data;
  }

  async uploadCoverImage(id: string | number, file: File): Promise<Church> {
    const formData = new FormData();
    formData.append('cover_image', file);

    const response = await api.post(`/churches/${id}/upload_cover/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  }

  async getDenominations(): Promise<Denomination[]> {
    const errors: any[] = [];

    // Try the churches endpoint first (it's the one that works)
    try {
      const response = await api.get('/churches/denominations/');
      const data = response.data;
      // The response is a direct array, not wrapped in results
      return Array.isArray(data) ? data : [];
    } catch (error: any) {
      errors.push({ endpoint: '/churches/denominations/', error: error.message || String(error) });
      console.warn('Failed to fetch from churches API:', error);
    }

    // Fallback to knowledge API endpoint
    try {
      const response = await api.get('/knowledge/denominations/');
      const data = response.data;
      return Array.isArray(data) ? data : (data.results || []);
    } catch (error: any) {
      errors.push({ endpoint: '/knowledge/denominations/', error: error.message || String(error) });
      console.warn('Failed to fetch from knowledge API:', error);
    }

    // If both fail, throw detailed error
    const errorDetails = {
      message: 'Failed to fetch denominations from all endpoints',
      component: 'ApiChurchService',
      method: 'getDenominations',
      attemptedEndpoints: ['/churches/denominations/', '/knowledge/denominations/'],
      errors: errors,
      timestamp: new Date().toISOString(),
      hint: 'Both primary and fallback endpoints failed - backend may be down or misconfigured'
    };
    console.error('[ApiChurchService] All endpoints failed:', errorDetails);
    throw new Error(JSON.stringify(errorDetails, null, 2));
  }

  async getDenominationContext(slug: string): Promise<{ slug: string; context: string }> {
    try {
      const response = await api.get(`/churches/denominations/${slug}/context/`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch denomination context:', error);
      return { slug, context: '' };
    }
  }

  async updateDenominationContext(slug: string, context: string): Promise<void> {
    await api.put(`/churches/denominations/${slug}/context/`, { context });
  }

  async searchByLocation(query: string, radius?: number): Promise<Church[]> {
    const params = new URLSearchParams({ query });
    if (radius) params.append('radius', String(radius));
    
    const response = await api.get(`/churches/search/location/?${params}`);
    const data = response.data;
    return data.results || data || [];
  }

  async exportChurchData(id: string | number, format: 'pdf' | 'excel' | 'csv'): Promise<Blob> {
    const response = await api.get(`/churches/${id}/export/`, {
      params: { format },
      responseType: 'blob'
    });
    return response.data;
  }

  // Event Management Methods

  async getEvents(churchId: string, filters?: EventFilters): Promise<Event[]> {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.myEvents) params.append('my_events', 'true');
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.eventType) params.append('event_type', filters.eventType);
      if (filters.ministry) params.append('ministry', filters.ministry);
    }
    
    const response = await api.get(`/churches/${churchId}/events/?${params}`);
    const data = response.data;
    const events = data.results || data || [];
    
    // Transform snake_case to camelCase
    return events.map((event: any) => ({
      id: event.id,
      title: event.title,
      eventType: event.event_type,
      date: event.date,
      startTime: event.start_time,
      endTime: event.end_time,
      church: event.church,
      ministry: event.ministry,
      ministryName: event.ministry_name,
      createdBy: event.created_by,
      createdByName: event.created_by_name,
      location: event.location,
      locationName: event.location_name,
      description: event.description,
      isPublic: event.is_public,
      maxAttendees: event.max_attendees,
      createdAt: event.created_at,
      updatedAt: event.updated_at
    }));
  }

  async getEvent(churchId: string, eventId: string): Promise<Event> {
    const response = await api.get(`/churches/${churchId}/events/${eventId}/`);
    const event = response.data;

    // Transform snake_case to camelCase
    return {
      id: event.id,
      title: event.title,
      eventType: event.event_type,
      date: event.date,
      startTime: event.start_time,
      endTime: event.end_time,
      church: event.church,
      ministry: event.ministry,
      ministryName: event.ministry_name,
      createdBy: event.created_by,
      createdByName: event.created_by_name,
      location: event.location,
      locationName: event.location_name,
      description: event.description,
      isPublic: event.is_public,
      maxAttendees: event.max_attendees,
      createdAt: event.created_at,
      updatedAt: event.updated_at
    };
  }

  async createEvent(churchId: string, event: CreateEventRequest): Promise<Event> {
    // Transform camelCase to snake_case for API
    const apiData: Record<string, any> = {
      title: event.title,
      event_type: event.eventType,
      date: event.date,
      start_time: event.startTime,
      end_time: event.endTime,
      ministry: event.ministry,
      location: event.location,
      description: event.description,
      is_public: event.isPublic !== undefined ? event.isPublic : true,
      max_attendees: event.maxAttendees,
      // Recurrence fields
      is_recurring: (event as any).isRecurring || false,
      recurrence_rule: (event as any).recurrenceRule || null
    };
    
    const response = await api.post(`/churches/${churchId}/events/`, apiData);
    const createdEvent = response.data;
    
    // Transform response back to camelCase
    return {
      id: createdEvent.id,
      title: createdEvent.title,
      eventType: createdEvent.event_type,
      date: createdEvent.date,
      startTime: createdEvent.start_time,
      endTime: createdEvent.end_time,
      church: createdEvent.church,
      ministry: createdEvent.ministry,
      ministryName: createdEvent.ministry_name,
      createdBy: createdEvent.created_by,
      createdByName: createdEvent.created_by_name,
      location: createdEvent.location,
      description: createdEvent.description,
      isPublic: createdEvent.is_public,
      maxAttendees: createdEvent.max_attendees,
      createdAt: createdEvent.created_at,
      updatedAt: createdEvent.updated_at
    };
  }

  async updateEvent(churchId: string, eventId: string, updates: UpdateEventRequest): Promise<Event> {
    // Transform camelCase to snake_case for API
    const apiData: any = {};
    if (updates.title !== undefined) apiData.title = updates.title;
    if (updates.eventType !== undefined) apiData.event_type = updates.eventType;
    if (updates.date !== undefined) apiData.date = updates.date;
    if (updates.startTime !== undefined) apiData.start_time = updates.startTime;
    if (updates.endTime !== undefined) apiData.end_time = updates.endTime;
    if (updates.ministry !== undefined) apiData.ministry = updates.ministry;
    if (updates.location !== undefined) apiData.location = updates.location;
    if (updates.description !== undefined) apiData.description = updates.description;
    if (updates.isPublic !== undefined) apiData.is_public = updates.isPublic;
    if (updates.maxAttendees !== undefined) apiData.max_attendees = updates.maxAttendees;
    
    const response = await api.patch(`/churches/${churchId}/events/${eventId}/`, apiData);
    const updatedEvent = response.data;
    
    // Transform response back to camelCase
    return {
      id: updatedEvent.id,
      title: updatedEvent.title,
      eventType: updatedEvent.event_type,
      date: updatedEvent.date,
      startTime: updatedEvent.start_time,
      endTime: updatedEvent.end_time,
      church: updatedEvent.church,
      ministry: updatedEvent.ministry,
      ministryName: updatedEvent.ministry_name,
      createdBy: updatedEvent.created_by,
      createdByName: updatedEvent.created_by_name,
      location: updatedEvent.location,
      description: updatedEvent.description,
      isPublic: updatedEvent.is_public,
      maxAttendees: updatedEvent.max_attendees,
      createdAt: updatedEvent.created_at,
      updatedAt: updatedEvent.updated_at
    };
  }

  async deleteEvent(churchId: string, eventId: string): Promise<void> {
    await api.delete(`/churches/${churchId}/events/${eventId}/`);
  }

  // Leadership Meeting Methods

  async createLeadershipMeeting(churchId: string, meeting: {
    event: CreateEventRequest;
    purpose: 'planning' | 'review' | 'emergency' | 'training';
    agenda: Array<{ item: string; duration: number; presenter?: string }>;
    requiredAttendees?: string[];
    optionalAttendees?: string[];
  }): Promise<any> {
    // Map frontend purpose to backend purpose choices
    const purposeMap: Record<string, string> = {
      'planning': 'strategic_planning',
      'review': 'ministry_review',
      'emergency': 'emergency',
      'training': 'regular'  // Training maps to regular meeting
    };

    // Backend expects event fields + meeting fields in single request
    const meetingData = {
      // Event fields (from CreateEventRequest)
      title: meeting.event.title,
      date: meeting.event.date,
      start_time: meeting.event.startTime,
      end_time: meeting.event.endTime,
      location: meeting.event.location,
      description: meeting.event.description || '',

      // Meeting fields
      purpose: purposeMap[meeting.purpose] || 'regular',
      agenda: meeting.agenda || [],
      required_attendees: meeting.requiredAttendees || [],
      optional_attendees: meeting.optionalAttendees || []
    };

    const response = await api.post(`/churches/${churchId}/events/leadership-meeting/`, meetingData);
    return response.data;
  }

  async getLeadershipMeetings(churchId: string, filters?: {
    purpose?: string;
    startDate?: string;
    endDate?: string;
    upcoming?: boolean;
  }): Promise<any[]> {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.purpose) params.append('purpose', filters.purpose);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.upcoming) params.append('upcoming', 'true');
    }
    
    const response = await api.get(`/churches/${churchId}/events/leadership-meetings/?${params}`);
    const data = response.data;
    return data.results || data || [];
  }

  async getUpcomingMeetings(churchId: string, days: number = 30): Promise<any[]> {
    const params = new URLSearchParams();
    params.append('upcoming_days', String(days));
    
    const response = await api.get(`/churches/${churchId}/upcoming-meetings/?${params}`);
    const data = response.data;
    return data.results || data || [];
  }

  async updateMeetingAgenda(churchId: string, meetingId: string, agenda: Array<{
    item: string;
    duration: number;
    presenter?: string;
    notes?: string;
  }>): Promise<any> {
    const response = await api.patch(
      `/churches/${churchId}/events/${meetingId}/`,
      { agenda }
    );
    return response.data;
  }

  async recordMeetingMinutes(churchId: string, meetingId: string, minutes: {
    notes: string;
    decisions: Array<{ description: string; responsible?: string; dueDate?: string }>;
    actionItems: Array<{ task: string; assignee: string; dueDate: string }>;
    nextMeetingDate?: string;
  }): Promise<any> {
    const response = await api.post(
      `/churches/${churchId}/events/${meetingId}/minutes/`,
      minutes
    );
    return response.data;
  }

  // Event Attendee Methods

  async updateRsvp(churchId: string, eventId: string, attendeeId: string, status: 'attending' | 'maybe' | 'declined'): Promise<any> {
    const response = await api.patch(
      `/churches/${churchId}/events/${eventId}/attendees/${attendeeId}/`,
      { rsvp_status: status }
    );
    return response.data;
  }

  async getEventAttendees(churchId: string, eventId: string, filters?: {
    rsvpStatus?: 'attending' | 'maybe' | 'declined' | 'no_response';
    memberType?: 'leader' | 'member' | 'guest';
  }): Promise<any[]> {
    const params = new URLSearchParams();
    if (filters) {
      if (filters.rsvpStatus) params.append('rsvp_status', filters.rsvpStatus);
      if (filters.memberType) params.append('member_type', filters.memberType);
    }
    
    const response = await api.get(`/churches/${churchId}/events/${eventId}/attendees/?${params}`);
    const data = response.data;
    return data.results || data || [];
  }

  async confirmAttendance(churchId: string, eventId: string, attendeeId: string, attended: boolean): Promise<any> {
    const response = await api.patch(
      `/churches/${churchId}/events/${eventId}/attendees/${attendeeId}/`,
      { attended }
    );
    return response.data;
  }

  // Church Profile Management Methods

  async getProfileCompletionStatus(): Promise<ProfileCompletionStatus> {
    try {
      const response = await api.get('/ai/church-profile/completion_status/');
      return response.data;
    } catch (error) {
      console.error('Failed to get profile completion status', { error });
      throw new Error('Failed to get profile completion status');
    }
  }

  async completeTier1(data: {
    church_size_category: ChurchSizeCategory;
    primary_focus: PrimaryFocus;
    biggest_challenge: BiggestChallenge;
  }): Promise<ProfileCompletionStatus> {
    try {
      const response = await api.post('/ai/church-profile/complete_tier1/', data);
      return response.data.completion_status;
    } catch (error) {
      console.error('Failed to complete Tier 1', { error });
      throw new Error('Failed to complete Tier 1 profile');
    }
  }

  async completeTier2(data: {
    mission_statement?: string;
    vision_statement?: string;
    top_priorities?: ChurchPriority[];
    growth_goals?: GrowthGoals;
    resource_level?: ResourceLevel;
  }): Promise<ProfileCompletionStatus> {
    try {
      const response = await api.post('/ai/church-profile/complete_tier2/', data);
      return response.data.completion_status;
    } catch (error) {
      console.error('Failed to complete Tier 2', { error });
      throw new Error('Failed to complete Tier 2 profile');
    }
  }

  async completeTier3(data: {
    smart_goals?: SmartGoal[];
    ministry_specific_priorities?: Record<string, any>;
    custom_kpis?: CustomKPI[];
  }): Promise<ProfileCompletionStatus> {
    try {
      const response = await api.post('/ai/church-profile/complete_tier3/', data);
      return response.data.completion_status;
    } catch (error) {
      console.error('Failed to complete Tier 3', { error });
      throw new Error('Failed to complete Tier 3 profile');
    }
  }

  async getSuggestedValues(): Promise<SuggestedDefaults> {
    try {
      const response = await api.get('/ai/church-profile/suggested_values/');
      return response.data;
    } catch (error) {
      console.error('Failed to get suggested values', { error });
      // Return default suggestions on error
      return {
        tier1: {
          suggested_focus: 'spiritual_growth',
          likely_challenge: 'volunteer_shortage'
        },
        tier2: {
          suggested_priorities: [],
          realistic_growth: {},
          mission_templates: []
        },
        tier3: {
          suggested_kpis: [],
          smart_goal_templates: []
        }
      };
    }
  }

  async updateProfile(updates: Partial<ChurchAIContext>): Promise<ProfileCompletionStatus> {
    try {
      const response = await api.patch('/ai/church-profile/update_profile/', updates);
      return response.data.completion_status;
    } catch (error) {
      console.error('Failed to update profile', { error });
      throw new Error('Failed to update church profile');
    }
  }

  async resetTier(tier: 1 | 2 | 3): Promise<ProfileCompletionStatus> {
    try {
      const response = await api.post('/ai/church-profile/reset_tier/', { tier });
      return response.data.completion_status;
    } catch (error) {
      console.error(`Failed to reset Tier ${tier}`, { error });
      throw new Error(`Failed to reset Tier ${tier}`);
    }
  }

  // ===== CHURCH LEADERSHIP MANAGEMENT =====

  /**
   * Get all leaders for a church
   * @param churchId Church ID
   * @returns Promise resolving to array of church leaders
   */
  async getLeadership(churchId: string): Promise<ChurchLeader[]> {
    try {
      const response = await api.get(`/churches/${churchId}/leaders/`);
      // Handle paginated or non-paginated response
      const leaders = response.data.results || response.data || [];
      return leaders;
    } catch (error) {
      console.error('Failed to load leadership', { error });
      return [];
    }
  }

  /**
   * Alias for getLeadership - returns paginated response format for components
   * @param churchId Church ID
   * @returns Promise resolving to paginated leader response
   */
  async getLeaders(churchId: string): Promise<{ results: ChurchLeader[]; count?: number }> {
    try {
      const response = await api.get(`/churches/${churchId}/leaders/`);
      // Return in paginated format expected by components
      if (response.data.results) {
        return response.data;
      }
      // Wrap array in paginated format
      return { results: response.data || [], count: (response.data || []).length };
    } catch (error) {
      console.error('Failed to load leaders', { error });
      return { results: [], count: 0 };
    }
  }

  /**
   * Get leaders organized by type (member vs hired staff)
   * @param churchId Church ID
   * @returns Promise resolving to leaders organized by type
   */
  async getLeadershipByType(churchId: string): Promise<{
    member_leaders: ChurchLeader[];
    hired_staff: ChurchLeader[];
  }> {
    try {
      const response = await api.get(`/churches/${churchId}/leaders/by-type/`);
      return response.data;
    } catch (error) {
      console.error('Failed to load leadership by type', { error });
      return { member_leaders: [], hired_staff: [] };
    }
  }

  /**
   * Get featured leaders for public display
   * @param churchId Church ID
   * @returns Promise resolving to array of featured leaders
   */
  async getFeaturedLeaders(churchId: string): Promise<ChurchLeader[]> {
    try {
      const response = await api.get(`/churches/${churchId}/leaders/featured/`);
      return response.data;
    } catch (error) {
      console.error('Failed to load featured leaders', { error });
      return [];
    }
  }

  /**
   * Get a single leader
   * @param churchId Church ID
   * @param leaderId Leader ID
   * @returns Promise resolving to leader details
   */
  async getLeader(churchId: string, leaderId: string): Promise<ChurchLeader> {
    const response = await api.get(`/churches/${churchId}/leaders/${leaderId}/`);
    return response.data;
  }

  /**
   * Add a new leadership member
   * @param churchId Church ID
   * @param leader Leader data
   * @returns Promise resolving to created leader
   */
  async addLeadershipMember(churchId: string, leader: ChurchLeaderRequest): Promise<ChurchLeader> {
    const response = await api.post(`/churches/${churchId}/leaders/`, leader);
    return response.data;
  }

  /**
   * Update a leadership member
   * @param churchId Church ID
   * @param leaderId Leader ID
   * @param updates Leader updates
   * @returns Promise resolving to updated leader
   */
  async updateLeadershipMember(churchId: string, leaderId: string, updates: Partial<ChurchLeaderRequest>): Promise<ChurchLeader> {
    const response = await api.patch(`/churches/${churchId}/leaders/${leaderId}/`, updates);
    return response.data;
  }

  /**
   * Delete a leadership member
   * @param churchId Church ID
   * @param leaderId Leader ID
   * @returns Promise resolving when deleted
   */
  async deleteLeadershipMember(churchId: string, leaderId: string): Promise<void> {
    await api.delete(`/churches/${churchId}/leaders/${leaderId}/`);
  }

  /**
   * Get available leader role options
   * @param churchId Church ID
   * @returns Promise resolving to role options
   */
  async getLeaderRoleOptions(churchId: string): Promise<{ value: string; label: string }[]> {
    try {
      const response = await api.get(`/churches/${churchId}/leaders/role-options/`);
      return response.data.roles || [];
    } catch (error) {
      console.error('Failed to load leader role options', { error });
      return [];
    }
  }

  /**
   * Get members eligible to become leaders
   * @param churchId Church ID
   * @returns Promise resolving to eligible members
   */
  async getEligibleLeaderMembers(churchId: string): Promise<{
    id: string;
    full_name: string;
    email?: string;
  }[]> {
    try {
      const response = await api.get(`/churches/${churchId}/leaders/eligible-members/`);
      return response.data.eligible_members || [];
    } catch (error) {
      console.error('Failed to load eligible members', { error });
      return [];
    }
  }

  /**
   * Reorder a leader's display position
   * @param churchId Church ID
   * @param leaderId Leader ID
   * @param displayOrder New display order
   * @returns Promise resolving when updated
   */
  async reorderLeader(churchId: string, leaderId: string, displayOrder: number): Promise<void> {
    await api.post(`/churches/${churchId}/leaders/${leaderId}/reorder/`, {
      display_order: displayOrder
    });
  }

  /**
   * Get employment status options
   * @param churchId Church ID
   * @returns Promise resolving to employment options
   */
  async getEmploymentOptions(churchId: string): Promise<{ value: string; label: string }[]> {
    try {
      const response = await api.get(`/churches/${churchId}/leaders/employment-options/`);
      return response.data.employment_options || [];
    } catch (error) {
      console.error('Failed to load employment options', { error });
      return [];
    }
  }

  /**
   * Get all roles for a leader (primary + additional)
   * @param churchId Church ID
   * @param leaderId Leader ID
   * @returns Promise resolving to roles data
   */
  async getLeaderRoles(churchId: string, leaderId: string): Promise<{
    leader_id: string;
    display_name: string;
    roles: Array<{
      id: string | null;
      role: string;
      role_display: string;
      custom_title: string;
      is_primary: boolean;
      is_active: boolean;
      start_date?: string;
      end_date?: string;
    }>;
    available_roles: Array<{ value: string; label: string }>;
  }> {
    const response = await api.get(`/churches/${churchId}/leaders/${leaderId}/roles/`);
    return response.data;
  }

  /**
   * Add an additional role to a leader
   * @param churchId Church ID
   * @param leaderId Leader ID
   * @param roleData Role data
   * @returns Promise resolving to created role
   */
  async addLeaderRole(churchId: string, leaderId: string, roleData: {
    role: string;
    custom_title?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<any> {
    const response = await api.post(`/churches/${churchId}/leaders/${leaderId}/roles/`, roleData);
    return response.data;
  }

  /**
   * Remove an additional role from a leader
   * @param churchId Church ID
   * @param leaderId Leader ID
   * @param roleId Role ID
   * @returns Promise resolving when deleted
   */
  async removeLeaderRole(churchId: string, leaderId: string, roleId: string): Promise<void> {
    await api.delete(`/churches/${churchId}/leaders/${leaderId}/roles/${roleId}/`);
  }

  /**
   * Link a leader to system access (ChurchStaff)
   * @param churchId Church ID
   * @param leaderId Leader ID
   * @param options Link options
   * @returns Promise resolving to staff access info
   */
  async linkLeaderStaffAccess(churchId: string, leaderId: string, options: {
    staff_id?: string;
    create_new?: boolean;
    role?: string;
  }): Promise<{
    status: string;
    staff_access: {
      id: string;
      user_email?: string;
      role: string;
    };
    message?: string;
  }> {
    const response = await api.post(`/churches/${churchId}/leaders/${leaderId}/link-staff-access/`, options);
    return response.data;
  }

  /**
   * Unlink a leader from system access
   * @param churchId Church ID
   * @param leaderId Leader ID
   * @returns Promise resolving when unlinked
   */
  async unlinkLeaderStaffAccess(churchId: string, leaderId: string): Promise<void> {
    await api.delete(`/churches/${churchId}/leaders/${leaderId}/unlink-staff-access/`);
  }

  /**
   * Get leadership position for a specific member
   * @param churchId Church ID
   * @param memberId Member ID
   * @returns Promise resolving to member's leadership data
   */
  async getMemberLeadership(churchId: string, memberId: string): Promise<{
    exists: boolean;
    leader: ChurchLeader | null;
    message?: string;
  }> {
    try {
      const response = await api.get(`/churches/${churchId}/leaders/by-member/${memberId}/`);
      return response.data;
    } catch (error) {
      console.error('Failed to get member leadership', { error });
      return { exists: false, leader: null };
    }
  }

  // ===== LEADERSHIP PANEL METHODS =====

  /**
   * Get leadership hierarchy tree for org chart visualization
   * @param churchId Church ID
   * @returns Promise resolving to hierarchy tree
   */
  async getHierarchyTree(churchId: string): Promise<{
    tree: Array<{
      id: string;
      name: string;
      title: string;
      leader_type: string;
      photo_url?: string | null;
      children: any[];
    }>;
  }> {
    const response = await api.get(`/churches/${churchId}/leaders/hierarchy-tree/`);
    return response.data;
  }

  /**
   * Set the reports_to relationship for a leader
   * @param churchId Church ID
   * @param leaderId Leader ID
   * @param reportsToId ID of leader to report to (or null)
   * @returns Promise resolving to updated hierarchy info
   */
  async setLeaderReportsTo(churchId: string, leaderId: string, reportsToId: string | null): Promise<{
    leader_id: string;
    leader_name: string;
    reports_to_id: string | null;
    reports_to_name: string | null;
    hierarchy_level: number;
  }> {
    const response = await api.post(`/churches/${churchId}/leaders/${leaderId}/set-reports-to/`, {
      reports_to_id: reportsToId
    });
    return response.data;
  }

  /**
   * Get all ministry leaders with their assignments
   * @param churchId Church ID
   * @returns Promise resolving to ministry leaders list
   */
  async getMinistryLeaders(churchId: string): Promise<{
    ministry_leaders: Array<{
      ministry_id: string;
      ministry_name: string;
      leader_id: string | null;
      leader_name: string;
      leader_title: string | null;
      parent_ministry: string | null;
      member_count: number;
    }>;
  }> {
    const response = await api.get(`/churches/${churchId}/leaders/ministry-leaders/`);
    return response.data;
  }

  /**
   * Atomically reassign a ministry to a different leader
   * @param churchId Church ID
   * @param ministryId Ministry ID
   * @param newLeaderId New leader member ID
   * @returns Promise resolving to reassignment result
   */
  async reassignMinistryLeader(churchId: string, ministryId: string, newLeaderId: string): Promise<{
    ministry_id: string;
    ministry_name: string;
    old_leader: { id: string | null; name: string | null };
    new_leader: { id: string; name: string };
  }> {
    const response = await api.post(`/churches/${churchId}/leaders/reassign-ministry/`, {
      ministry_id: ministryId,
      new_leader_id: newLeaderId
    });
    return response.data;
  }

  /**
   * Get all custom titles for the church
   * @param churchId Church ID
   * @param includeInactive Include inactive titles
   * @returns Promise resolving to custom titles list
   */
  async getCustomTitles(churchId: string, includeInactive = false): Promise<{
    custom_titles: Array<{
      id: string;
      name: string;
      system_role: string;
      description: string;
      is_active: boolean;
      display_order: number;
    }>;
  }> {
    const response = await api.get(`/churches/${churchId}/leaders/custom-titles/`, {
      params: { include_inactive: includeInactive ? 'true' : 'false' }
    });
    return response.data;
  }

  /**
   * Create a custom title for the church
   * @param churchId Church ID
   * @param data Title creation data
   * @returns Promise resolving to created title
   */
  async createCustomTitle(churchId: string, data: {
    name: string;
    system_role: string;
    description?: string;
    display_order?: number;
  }): Promise<{
    id: string;
    name: string;
    system_role: string;
    description: string;
    display_order: number;
    is_active: boolean;
  }> {
    const response = await api.post(`/churches/${churchId}/leaders/create-custom-title/`, data);
    return response.data;
  }

  /**
   * Set or update the custom title for a leader
   * @param churchId Church ID
   * @param leaderId Leader ID
   * @param customTitleId Custom title ID (or null to clear)
   * @returns Promise resolving to updated leader
   */
  async setLeaderCustomTitle(churchId: string, leaderId: string, customTitleId: string | null): Promise<any> {
    const response = await api.post(`/churches/${churchId}/leaders/${leaderId}/set-custom-title/`, {
      custom_title_id: customTitleId
    });
    return response.data;
  }

  /**
   * Get leadership roles for a denomination (system roles with denomination-specific names)
   * @param denominationProfileId Denomination profile ID
   * @returns Promise resolving to denomination roles
   */
  async getDenominationLeadershipRoles(denominationProfileId: string): Promise<{
    denomination_id: string;
    denomination_name: string | null;
    system_roles: Array<{
      code: string;
      system_name: string;
      display_name: string;
      is_denomination_override: boolean;
      is_additional_role: boolean;
    }>;
    additional_roles: Array<{
      code: string;
      system_name: string;
      display_name: string;
      is_denomination_override: boolean;
      is_additional_role: boolean;
      inherits_from?: string;
      description?: string;
      rank?: number;
    }>;
  }> {
    const response = await api.get(`/knowledge/denominations/${denominationProfileId}/leadership_roles/`);
    return response.data;
  }

  /**
   * Add a denomination-specific leadership role
   * @param denominationProfileId Denomination profile ID
   * @param data Role creation data
   * @returns Promise resolving when role is added
   */
  async addDenominationLeadershipRole(denominationProfileId: string, data: {
    code: string;
    name: string;
    description?: string;
    inherits_from?: string;
    rank?: number;
  }): Promise<{ message: string; role: any }> {
    const response = await api.post(`/knowledge/denominations/${denominationProfileId}/add_leadership_role/`, data);
    return response.data;
  }

  /**
   * Remove a denomination-specific leadership role
   * @param denominationProfileId Denomination profile ID
   * @param roleCode Role code to remove
   * @returns Promise resolving when role is removed
   */
  async removeDenominationLeadershipRole(denominationProfileId: string, roleCode: string): Promise<{ message: string }> {
    const response = await api.delete(`/knowledge/denominations/${denominationProfileId}/remove_leadership_role/`, {
      data: { role_code: roleCode }
    });
    return response.data;
  }

  /**
   * Update the display name mapping for a system role
   * @param denominationProfileId Denomination profile ID
   * @param systemRole System role code
   * @param displayName Denomination-specific display name
   * @returns Promise resolving when mapping is updated
   */
  async updateDenominationRoleMapping(denominationProfileId: string, systemRole: string, displayName: string): Promise<{ message: string }> {
    const response = await api.post(`/knowledge/denominations/${denominationProfileId}/update_role_mapping/`, {
      system_role: systemRole,
      display_name: displayName
    });
    return response.data;
  }

}